import _ from 'lodash';

import {addQueryToPath} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {compareName} from '../../../../srcCommon/helpers/localeHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {CrmSyncMapping} from '../../../components/accounts/crm/common/crmSyncHelpers';
import {checkIsHubspotConnected, checkIsSalesforceConnected} from '../../../helpers/integrationHelpers';
import {ActionBase, ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {ApiCrmFields, CrmFieldModel} from '../../../models/api/crmFieldModel';
import {ApiCrmHubspotFields, CrmHubspotFieldModel} from '../../../models/api/crmHubspotFieldModel';
import {ApiCrmHubspotMetadata, CrmHubspotMetadataModel} from '../../../models/api/crmHubspotMetadataModel';
import {ApiCrmList, CrmListModel} from '../../../models/api/crmListModel';
import {ApiCrmMetadata, CrmMetadataModel} from '../../../models/api/crmMetadataModel';
import {ApiCrmSalesforceFields, CrmSalesforceFieldModel} from '../../../models/api/crmSalesforceFieldModel';
import {ApiCrmSalesforceList, CrmSalesforceListModel} from '../../../models/api/crmSalesforceListModel';
import {
  ApiCrmSalesforceMetadata,
  CrmSalesforceMetadataModel,
} from '../../../models/api/crmSalesforceMetadataModel';
import {ApiCrmSyncSearchLists, CrmSyncSearchListsModel} from '../../../models/api/crmSearchListsModel';
import {
  ApiCrmSyncSearchSalesforceLists,
  CrmSyncSearchSalesforceListsModel,
} from '../../../models/api/crmSearchSalesforceLists';
import {
  ApiCrmSync,
  ApiCrmSyncs,
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncModel,
  CrmSyncSourcesEnum,
  CrmSyncStatusesEnum,
} from '../../../models/api/crmSyncModel';
import {ApiSendOptions, fetchApiAsync, postApiAsync, putApiAsync} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {ResourceTableStatusesEnum} from '../../appTypes';
import {getCompanyUrl} from '../company/companySelectors';
import {updateDataAction} from '../dataGroupsActions';
import {updateIntegrationAction} from '../integrations/integrationsActions';
import {getHubspotIntegration, getSalesforceIntegration} from '../integrations/integrationSelectors';
import {jobUpdatedAction} from '../jobs/jobsActions';
import {
  getCrmSyncs,
  getCrmSyncsAsyncStatus,
  getFlatCrmSyncs,
  makeGetCrmSyncViaSourceAndResourceType,
  makeGetEnabledIntegrationViaSource,
} from './crmSyncSelectors';

/*
 * Types.
 */

export interface CrmSyncSearchOptions extends ApiSendOptions {
  limit: number;
  offset?: number;
  nextPageToken?: string;
}

/*
 * Action enum.
 */

export enum CrmSyncActionsEnum {
  CRM_SYNC_DELETED = '@@data/crmSync/CRM_SYNC_DELETED',
  FETCH_CRM_SYNCS = '@@data/crmSync/FETCH_CRM_SYNCS',
  FETCH_CRM_SYNCS_COMPLETED = '@@data/crmSync/FETCH_CRM_SYNCS_COMPLETED',
  FETCH_CRM_SYNCS_FAILED = '@@data/crmSync/FETCH_CRM_SYNCS_FAILED',
}

/*
 * Action Types.
 */

export interface CrmSyncDeletedAction extends ActionWithPayload<number> {
  type: CrmSyncActionsEnum.CRM_SYNC_DELETED;
}

export interface FetchCrmSyncsAction extends ActionBase {
  type: CrmSyncActionsEnum.FETCH_CRM_SYNCS;
}

export interface FetchCrmSyncsCompletedAction extends ActionWithPayload<ReadonlyArray<CrmSyncModel>> {
  type: CrmSyncActionsEnum.FETCH_CRM_SYNCS_COMPLETED;
}

export interface FetchCrmSyncsFailedAction extends ActionBase {
  type: CrmSyncActionsEnum.FETCH_CRM_SYNCS_FAILED;
}

/*
 * Union Action type.
 */

export type CrmSyncAction =
  | FetchCrmSyncsAction
  | FetchCrmSyncsCompletedAction
  | FetchCrmSyncsFailedAction
  | CrmSyncDeletedAction;

/*
 * Action creators.
 */

function fetchCrmSyncsAction(): FetchCrmSyncsAction {
  return {
    type: CrmSyncActionsEnum.FETCH_CRM_SYNCS,
  };
}

function fetchCrmSyncsActionCompleted(crmSyncs: ReadonlyArray<CrmSyncModel>): FetchCrmSyncsCompletedAction {
  return {
    type: CrmSyncActionsEnum.FETCH_CRM_SYNCS_COMPLETED,
    payload: crmSyncs,
  };
}

function fetchCrmSyncsActionFailed(): FetchCrmSyncsFailedAction {
  return {
    type: CrmSyncActionsEnum.FETCH_CRM_SYNCS_FAILED,
  };
}

function crmSyncDeletedAction(crmSyncId: number): CrmSyncDeletedAction {
  return {
    type: CrmSyncActionsEnum.CRM_SYNC_DELETED,
    payload: crmSyncId,
  };
}

/*
 * Actions.
 */

export function deleteCrmSyncAction(crmSyncId: number): ThunkAction {
  return (dispatch, getState) => {
    const crmSyncs = getCrmSyncs(getState());
    const existingCrmSync = crmSyncs[crmSyncId];

    // If we do not have a crm sync, do nothing.
    if (!existingCrmSync) {
      return undefined;
    }

    return dispatch(crmSyncDeletedAction(crmSyncId));
  };
}

function computeShouldFetchCrmSyncsAction(): ThunkAction<boolean> {
  return (_dispatch, getState) => {
    const {status, isStale} = getCrmSyncsAsyncStatus(getState());

    // If the store is marked as stale, we should refresh.
    if (isStale) {
      return true;
    }

    // If the status is either failed or initial state, we should fetch crm syncs.
    if (status === ResourceTableStatusesEnum.INITIAL || status === ResourceTableStatusesEnum.FAILED) {
      return true;
    }

    // We can just rely on the redux state currently.
    return false;
  };
}

export function fetchCrmSyncsAsyncAction(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Check if we should fetch.
    const shouldFetch = dispatch(computeShouldFetchCrmSyncsAction());
    if (!shouldFetch) {
      return;
    }

    // Dispatch that we are loading crm syncs.
    dispatch(fetchCrmSyncsAction());

    try {
      const companyUrl = getCompanyUrl(getState());
      const {crmSyncs, integrations, jobs} = await fetchApiAsync(ApiCrmSyncs, `${companyUrl}/syncs`);
      await batch(() => {
        dispatch(fetchCrmSyncsActionCompleted(crmSyncs));
        jobs.forEach((job) => {
          dispatch(jobUpdatedAction(job));
        });
        integrations.forEach((integration) => {
          dispatch(updateIntegrationAction(integration));
        });
      });
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      dispatch(fetchCrmSyncsActionFailed());
    }
  };
}

export function fetchOrCreateCrmSyncResourceAsyncAction(
  source: CrmSyncSourcesEnum,
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  options: ApiSendOptions,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const integration = makeGetEnabledIntegrationViaSource(source)(getState());

    if (!integration) {
      return;
    }

    // Check to see if we already have an existing crm sync in the store.
    const existingCrmSync = getFlatCrmSyncs(getState()).find(
      (crmSync) => crmSync.source === source && crmSync.resourceType === resourceType,
    );
    if (existingCrmSync) {
      // Make sure we are up to date with the latest information, fetch in the background.
      dispatch(
        searchForCrmSyncViaIntegrationIdAndResourceTypeAsyncAction(integration.id, resourceType, options),
      );
      return;
    }

    // Search for an existing crm sync resource.
    const searchCrmSyncId = await dispatch(
      searchForCrmSyncViaIntegrationIdAndResourceTypeAsyncAction(integration.id, resourceType, options),
    );
    if (searchCrmSyncId) {
      return;
    }

    // We failed to find a crm sync object, create a new one.
    await dispatch(createCrmSyncAsyncAction(source, resourceType, options));
  };
}

export function searchForCrmSyncViaIntegrationIdAndResourceTypeAsyncAction(
  integrationId: number,
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  options: ApiSendOptions,
): ThunkAsyncAction<number | undefined> {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const searchUrl = addQueryToPath(`${companyUrl}/syncs/search`, {
      integration_id: integrationId,
      resource_type: resourceType,
    });

    try {
      const {crmSync, integration, job} = await fetchApiAsync(ApiCrmSync, searchUrl, options);
      options.cancelToken?.throwIfRequested();
      await batch(() => {
        dispatch(updateDataAction({crmSyncs: [crmSync]}));
        dispatch(updateIntegrationAction(integration));
        if (job) {
          dispatch(jobUpdatedAction(job));
        }
      });
      return crmSync.id;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      return undefined;
    }
  };
}

export function createCrmSyncAsyncAction(
  source: CrmSyncSourcesEnum,
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  options: ApiSendOptions,
): ThunkAsyncAction<number | undefined> {
  return async (dispatch, getState) => {
    const existingIntegration = makeGetEnabledIntegrationViaSource(source)(getState());
    if (!existingIntegration) {
      return undefined;
    }

    const companyUrl = getCompanyUrl(getState());

    try {
      const {crmSync, integration} = await postApiAsync(
        ApiCrmSync,
        `${companyUrl}/syncs`,
        {
          integration_id: existingIntegration.id,
          resource_type: resourceType,
        },
        options,
      );
      options.cancelToken?.throwIfRequested();
      await batch(() => {
        dispatch(updateDataAction({crmSyncs: [crmSync]}));
        dispatch(updateIntegrationAction(integration));
      });
      return crmSync.id;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      return undefined;
    }
  };
}

export function fetchSalesforceFieldsAsyncAction(
  source: CrmSyncSourcesEnum,
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  options: ApiSendOptions,
): ThunkAsyncAction<ReadonlyArray<CrmSalesforceFieldModel>> {
  return async (_dispatch, getState) => {
    const salesforceIntegration = getSalesforceIntegration(getState());
    const salesforceCrmSync = makeGetCrmSyncViaSourceAndResourceType()(getState(), {source, resourceType});

    // Make sure salesforce is connected and we have a crm sync object.
    if (!salesforceCrmSync || !checkIsSalesforceConnected(salesforceIntegration)) {
      throw new Error('Salesforce is not connected.');
    }

    const companyUrl = getCompanyUrl(getState());

    try {
      const fields = await fetchApiAsync(
        ApiCrmSalesforceFields,
        `${companyUrl}/syncs/${salesforceCrmSync.id}/fields`,
        options,
      );
      options.cancelToken?.throwIfRequested();
      return _([...fields])
        .sort((a, b) => compareName(a.label.toLowerCase(), b.label.toLowerCase()))
        .value();
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchMetadataAsyncAction(
  crmSyncId: number,
  options?: ApiSendOptions,
): ThunkAsyncAction<CrmMetadataModel | undefined> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    try {
      const metadata = await fetchApiAsync(
        ApiCrmMetadata,
        `${companyUrl}/syncs/${crmSyncId}/metadata`,

        options,
      );
      options?.cancelToken?.throwIfRequested();

      return metadata;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchSalesforceFieldMetadataAsyncAction(
  source: CrmSyncSourcesEnum,
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  options: ApiSendOptions,
): ThunkAsyncAction<CrmSalesforceMetadataModel | undefined> {
  return async (_dispatch, getState) => {
    const salesforceCrmSync = makeGetCrmSyncViaSourceAndResourceType()(getState(), {source, resourceType});

    // Make sure salesforce is connected and we have a crm sync object.
    if (!salesforceCrmSync) {
      return undefined;
    }

    const companyUrl = getCompanyUrl(getState());

    try {
      const metadata = await fetchApiAsync(
        ApiCrmSalesforceMetadata,
        `${companyUrl}/syncs/${salesforceCrmSync.id}/metadata`,
        options,
      );
      options.cancelToken?.throwIfRequested();
      return metadata;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchHubspotFieldMetadataAsyncAction(
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  options: ApiSendOptions,
): ThunkAsyncAction<CrmHubspotMetadataModel | undefined> {
  return async (_dispatch, getState) => {
    const hubspotCrmSync = makeGetCrmSyncViaSourceAndResourceType()(getState(), {
      source: CrmSyncSourcesEnum.HUBSPOT,
      resourceType,
    });

    // Make sure salesforce is connected and we have a crm sync object.
    if (!hubspotCrmSync) {
      return undefined;
    }

    const companyUrl = getCompanyUrl(getState());

    try {
      const metadata = await fetchApiAsync(
        ApiCrmHubspotMetadata,
        `${companyUrl}/syncs/${hubspotCrmSync.id}/metadata`,
        options,
      );
      options.cancelToken?.throwIfRequested();
      return metadata;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchHubspotFieldsAsyncAction(
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  options: ApiSendOptions,
): ThunkAsyncAction<ReadonlyArray<CrmHubspotFieldModel>> {
  return async (_dispatch, getState) => {
    const hubspotIntegration = getHubspotIntegration(getState());
    const hubspotCrmSync = makeGetCrmSyncViaSourceAndResourceType()(getState(), {
      source: CrmSyncSourcesEnum.HUBSPOT,
      resourceType,
    });

    // Make sure hubspot is connected and we have a crm sync object.
    if (!hubspotCrmSync || !checkIsHubspotConnected(hubspotIntegration)) {
      return [];
    }

    const companyUrl = getCompanyUrl(getState());

    try {
      const fields = await fetchApiAsync(
        ApiCrmHubspotFields,
        `${companyUrl}/syncs/${hubspotCrmSync.id}/fields`,
        options,
      );
      options.cancelToken?.throwIfRequested();
      return _([...fields])
        .sort((a, b) => compareName(a.label.toLowerCase(), b.label.toLowerCase()))
        .value();
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchFieldsAsyncAction(
  crmSyncId: number,
  options?: ApiSendOptions,
): ThunkAsyncAction<ReadonlyArray<CrmFieldModel>> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    try {
      const fields = await fetchApiAsync(ApiCrmFields, `${companyUrl}/syncs/${crmSyncId}/fields`, options);
      options?.cancelToken?.throwIfRequested();
      return _([...fields])
        .sort((a, b) => compareName(a.label.toLowerCase(), b.label.toLowerCase()))
        .value();
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

// TODO: remove this function and use searchListsAsyncAction
export function searchSalesforceAccountListsAsyncAction(
  source: CrmSyncSourcesEnum,
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  query: string,
  options: CrmSyncSearchOptions,
): ThunkAsyncAction<CrmSyncSearchSalesforceListsModel | undefined> {
  return async (_dispatch, getState) => {
    const salesforceIntegration = getSalesforceIntegration(getState());
    const salesforceCrmSync = makeGetCrmSyncViaSourceAndResourceType()(getState(), {source, resourceType});
    if (!salesforceIntegration || !salesforceCrmSync) {
      return undefined;
    }

    // Construct the search url.
    const companyUrl = getCompanyUrl(getState());
    const searchUrl = addQueryToPath(buildSearchListsUrl(companyUrl, salesforceCrmSync.id, query), {
      limit: options.limit,
      offset: options.offset,
    });

    try {
      const response = await fetchApiAsync(ApiCrmSyncSearchSalesforceLists, searchUrl, options);
      options.cancelToken?.throwIfRequested();
      return response;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      return undefined;
    }
  };
}

export function searchListsAsyncAction(
  source: CrmSyncSourcesEnum,
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  query: string,
  options: CrmSyncSearchOptions,
): ThunkAsyncAction<CrmSyncSearchListsModel | undefined> {
  return async (_dispatch, getState) => {
    const crmSync = makeGetCrmSyncViaSourceAndResourceType()(getState(), {source, resourceType});
    if (!crmSync) {
      return undefined;
    }

    // Construct the search url.
    const companyUrl = getCompanyUrl(getState());
    const searchUrl = addQueryToPath(`${companyUrl}/syncs/${crmSync.id}/search/lists`, {
      query,
      next_page_token: options.nextPageToken,
      limit: options.limit,
    });

    try {
      const response = await fetchApiAsync(ApiCrmSyncSearchLists, searchUrl, options);
      options.cancelToken?.throwIfRequested();
      return response;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      return undefined;
    }
  };
}

// TODO: remove this function once we use fetchListAsyncAction everywhere
export function fetchSalesforceAccountListAsyncAction(
  listId: string,
  resourceType: CrmSyncIntegrationResourceTypesEnum = CrmSyncIntegrationResourceTypesEnum.ACCOUNT,
): ThunkAsyncAction<CrmSalesforceListModel> {
  return async (_dispatch, getState) => {
    const salesforceIntegration = getSalesforceIntegration(getState());
    const salesforceCrmSync = makeGetCrmSyncViaSourceAndResourceType()(getState(), {
      source: CrmSyncSourcesEnum.SALESFORCE,
      resourceType,
    });
    if (!salesforceIntegration || !salesforceCrmSync) {
      throw new Error('User does not have salesforce connected.');
    }

    // Construct the search url.
    const companyUrl = getCompanyUrl(getState());
    const fetchUrl = `${companyUrl}/syncs/${salesforceCrmSync.id}/list/${listId}`;

    const response = await fetchApiAsync(ApiCrmSalesforceList, fetchUrl);
    return response;
  };
}

export function fetchListAsyncAction(listId: string, crmSyncId: number): ThunkAsyncAction<CrmListModel> {
  return async (_dispatch, getState) => {
    // Construct the search url.
    const companyUrl = getCompanyUrl(getState());
    const fetchUrl = `${companyUrl}/syncs/${crmSyncId}/list/${listId}`;

    const response = await fetchApiAsync(ApiCrmList, fetchUrl);
    return response;
  };
}

export function updateCrmSyncAsyncAction(
  crmSyncId: number,
  mapping: CrmSyncMapping,
  listId?: string,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    try {
      const {crmSync} = await putApiAsync(ApiCrmSync, `${companyUrl}/syncs/${crmSyncId}`, {
        metadata: {
          external_list_id: listId,
        },
        mapping,
      });
      dispatch(updateDataAction({crmSyncs: [crmSync]}));
      // If we are not currently enabled, we should enable syncing.
      if (crmSync.status !== CrmSyncStatusesEnum.ENABLED) {
        await dispatch(enableCrmSyncAsyncAction(crmSync.id));
      }
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      // TODO: Show error banner.
    }
  };
}

export function enableCrmSyncAsyncAction(syncId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    try {
      const {crmSync} = await postApiAsync(ApiCrmSync, `${companyUrl}/syncs/${syncId}/enable`, {});
      dispatch(updateDataAction({crmSyncs: [crmSync]}));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
    }
  };
}

export function disableCrmSyncAsyncAction(syncId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    try {
      const {crmSync} = await postApiAsync(ApiCrmSync, `${companyUrl}/syncs/${syncId}/disable`, {});
      dispatch(updateDataAction({crmSyncs: [crmSync]}));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
    }
  };
}

/*
 * Helpers.
 */

function buildSearchListsUrl(companyUrl: string, syncId: number, query: string) {
  if (query.trim() === '') {
    return `${companyUrl}/syncs/${syncId}/lists`;
  }
  return `${companyUrl}/syncs/${syncId}/search/lists/${query}`;
}
